/**
 * Division Page Data Hook
 * Pattern: Same as useUserPageData.ts, useSupplierPageData.ts, useContractPageData.ts
 *
 * Provides:
 * - Data fetching with React Query
 * - Filter counts with flicker prevention
 * - Pagination calculations
 * - Refresh function
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { DivisionsResponse, DivisionStatusTotals, Division } from '@app-types/division';

interface UseDivisionPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<DivisionsResponse>;
  enabled?: boolean;
}

interface UseDivisionPageDataReturn {
  divisions: Division[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  response: DivisionsResponse | undefined;
  filterCounts: Record<string, number>;
  totalItems: number;
  totalPages: number;
  refreshData: () => void;
}

export function useDivisionPageData(options: UseDivisionPageDataOptions): UseDivisionPageDataReturn {
  const { queryKey, queryFn, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch with React Query
  const { data: response, isLoading, error, isFetching } = useQuery<DivisionsResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData, // Prevents UI flicker
    enabled,
  });

  const divisions = useMemo(() => response?.data || [], [response]);

  // Preserve last known totals to avoid counter flicker
  const [lastStatusTotals, setLastStatusTotals] = useState<DivisionStatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;
    if (!totals) {
      return {
        all: response?.total || 0,
        active: 0,
        inactive: 0,
      };
    }
    return {
      all: totals.all || 0,
      active: totals.active || 0,
      inactive: totals.inactive || 0,
    };
  }, [response, lastStatusTotals]);

  const totalItems = response?.total || 0;
  const pageSize = response?.limit || 50;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    divisions,
    isLoading,
    error: error as Error | null,
    isFetching,
    response,
    filterCounts,
    totalItems,
    totalPages,
    refreshData,
  };
}
